/**
 * Columnas tipo UPC/EAN/GTIN: leer valor completo desde SheetJS (`raw: true`) y exportar como texto en Excel (`@`).
 */
/* global BigInt */

/** Igual que `normalizeCellKey` en `salesOrderBulkPreview` (evita depender de ese módulo aquí). */
function normalizeHeaderKeyForLongId(k) {
  return String(k || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cabeceras normalizadas que identifican columnas de código largo. */
const LONG_IDENTIFIER_HEADER_KEYS = new Set(
  ['UPC', 'EAN', 'GTIN', 'CODIGO DE BARRAS', 'CODIGO BARRAS', 'BARCODE'].map((k) => normalizeHeaderKeyForLongId(k))
);

/**
 * @param {unknown} cell - celda de la fila cabecera
 * @returns {boolean}
 */
export function headerCellIsLongIdentifierColumn(cell) {
  const n = normalizeHeaderKeyForLongId(String(cell ?? '').trim());
  if (!n) return false;
  return LONG_IDENTIFIER_HEADER_KEYS.has(n);
}

/**
 * @param {string[]} headerRow
 * @returns {Set<number>}
 */
export function getLongIdentifierColumnIndicesFromHeaderRow(headerRow) {
  const set = new Set();
  if (!Array.isArray(headerRow)) return set;
  headerRow.forEach((cell, ci) => {
    if (headerCellIsLongIdentifierColumn(cell)) set.add(ci);
  });
  return set;
}

/**
 * Convierte el valor en bruto de Excel a string de dígitos sin notación científica.
 * @param {unknown} rawValue
 * @returns {string|null} - `null` = no sustituir la celda mostrada
 */
export function formatLongIdentifierForGridCell(rawValue) {
  if (rawValue == null || rawValue === '') return null;
  if (rawValue instanceof Date) return null;

  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    const n = Math.trunc(rawValue);
    if (!Number.isFinite(n)) return null;
    if (Math.abs(rawValue - n) > 1e-9 && rawValue !== n) return null;
    if (Math.abs(n) <= Number.MAX_SAFE_INTEGER) return String(n);
    try {
      return BigInt(n).toString();
    } catch {
      return String(n);
    }
  }

  if (typeof rawValue === 'string') {
    const t = rawValue.trim();
    if (!t) return null;
    return t;
  }

  return String(rawValue);
}

/**
 * Sustituye celdas de columnas UPC/EAN/… con el valor derivado de `rowsRaw` (lectura `raw: true`).
 *
 * @param {string[][]} rows - matriz ya normalizada (fechas, etc.)
 * @param {unknown[][]} rowsRaw - misma forma que `rows`
 * @param {number} headerIndex - fila cabecera 0-based
 * @returns {string[][]}
 */
export function patchBulkGridLongIdentifierColumns(rows, rowsRaw, headerIndex) {
  if (!Array.isArray(rows) || !rows.length) return rows;
  const hi = Number(headerIndex);
  if (!Number.isFinite(hi) || hi < 0 || hi >= rows.length) {
    return rows.map((r) => (Array.isArray(r) ? [...r] : []));
  }
  const headerRow = rows[hi] || [];
  const colSet = getLongIdentifierColumnIndicesFromHeaderRow(headerRow);
  if (colSet.size === 0) return rows.map((r) => (Array.isArray(r) ? [...r] : []));

  const raw = Array.isArray(rowsRaw) ? rowsRaw : [];

  return rows.map((row, ri) => {
    if (!Array.isArray(row)) return [];
    if (ri <= hi) return [...row];
    const out = [...row];
    const rawRow = raw[ri];
    for (const ci of colSet) {
      const rv = rawRow && Array.isArray(rawRow) ? rawRow[ci] : undefined;
      const formatted = formatLongIdentifierForGridCell(rv);
      if (formatted != null && formatted !== '') {
        out[ci] = formatted;
      }
    }
    return out;
  });
}
